import {
	Button,
	ButtonGroup,
	Card,
	IconButton,
	LuIcon,
	Menu,
	Separator,
	stopPropagation,
	Text,
} from "@overmap-ai/blocks"
import { type IssueType, type Stored, EMPTY_ARRAY } from "@overmap-ai/common"
import {
	selectIssueTypeIdentifiersOfIssueType,
	selectIssueTypePrioritiesOfIssueType,
	selectIssueTypeStatusesOfIssueType,
	selectLatestIssueTypeFieldsOfIssueType,
	selectUsersOfProject,
} from "@overmap-ai/core"
import * as RadixCollapsible from "@radix-ui/react-collapsible"
import { useNavigate } from "@tanstack/react-router"
import { cx } from "class-variance-authority"
import { memo, type MouseEventHandler, useCallback, useMemo } from "react"

import { interactiveCardCva } from "../../../cva"
import { FilterRenderer } from "../../../filtering/components"
import type { AnyFilter } from "../../../filtering/filter"
import { useFilterManager } from "../../../filtering/hooks"
import { getIssueDataFilterSchema } from "../../../filtering/schemas"
import { modelIconMapping } from "../../../mappings/icons"
import {
	hideIssueType,
	setIssueTypeMapFilters,
	unhideIssueType,
	useAppDispatch,
	useAppSelector,
	useShallowEqaulSelector,
} from "../../../store"
import { selectActiveProjectId, selectIsIssueTypeHidden, selectIssueTypeMapFiltersById } from "../../../store/selectors"
import { selectIssuesOfProjectAndIssueType } from "../../../store/selectors/issues"
import type { IssueData } from "../../../typings"
import { IssueDataFilterMenu } from "../../FilterMenus/IssueDataFilterMenu"
import { useDeserializeFieldSchema } from "../../Form/hooks"
import { IssueTypeIcon } from "../../Icons/IssueTypeIcon"

interface IssueTypeVisibilityItemProps {
	issueType: Stored<IssueType>
}

export const IssueTypeVisibilityItem = memo<IssueTypeVisibilityItemProps>((props) => {
	const { issueType } = props

	const activeProjectId = useAppSelector(selectActiveProjectId)!
	const navigate = useNavigate()
	const dispatch = useAppDispatch()
	const issues = useShallowEqaulSelector(selectIssuesOfProjectAndIssueType(activeProjectId, issueType.uuid))
	const latestIssueTypeFields = useAppSelector(selectLatestIssueTypeFieldsOfIssueType(issueType.uuid))
	const issueTypeIdentifiers = useShallowEqaulSelector(selectIssueTypeIdentifiersOfIssueType(issueType.uuid))
	const isHidden = useAppSelector(selectIsIssueTypeHidden(issueType.uuid))
	const serializedFilters = useAppSelector(selectIssueTypeMapFiltersById(issueType.uuid))
	const issueTypePriorities = useShallowEqaulSelector(selectIssueTypePrioritiesOfIssueType(issueType.uuid))
	const issueTypeStatuses = useShallowEqaulSelector(selectIssueTypeStatusesOfIssueType(issueType.uuid))
	const users = useShallowEqaulSelector(selectUsersOfProject(activeProjectId))

	const fieldsSchema = useDeserializeFieldSchema(latestIssueTypeFields?.fields ?? EMPTY_ARRAY)

	const filterSchema = useMemo(
		() =>
			getIssueDataFilterSchema({
				assignees: users,
				issueTypeIdentifiers,
				issueTypeFieldSchema: fieldsSchema,
				issueTypePriorities,
				issueTypeStatuses,
				users,
			}),
		[fieldsSchema, issueTypeIdentifiers, issueTypePriorities, issueTypeStatuses, users],
	)

	const filters = useMemo(() => {
		return filterSchema.deserializeFilters(serializedFilters)
	}, [filterSchema, serializedFilters])

	const handleFiltersChange = useCallback(
		(filters: AnyFilter[]) => {
			dispatch(
				setIssueTypeMapFilters({
					id: issueType.uuid,
					filters: filterSchema.serializeFilters(filters),
				}),
			)
		},
		[dispatch, filterSchema, issueType.uuid],
	)

	const filterManager = useFilterManager<IssueData>({
		filterSchema,
		filters,
		onFilterChange: handleFiltersChange,
	})

	const handleVisibilityButtonClicked = useCallback<MouseEventHandler>(
		(e) => {
			e.preventDefault()
			e.stopPropagation()

			dispatch(isHidden ? unhideIssueType(issueType.uuid) : hideIssueType(issueType.uuid))
		},
		[issueType.uuid, dispatch, isHidden],
	)

	const handleCollapsibleOpenChange = useCallback(() => {
		dispatch(isHidden ? unhideIssueType(issueType.uuid) : hideIssueType(issueType.uuid))
	}, [dispatch, isHidden, issueType.uuid])

	const handleView = useCallback(() => {
		void navigate({
			from: "/$organizationId/$projectId",
			to: "/$organizationId/$projectId/issue-types/$issueTypeId",
			params: (prev) => ({ ...prev, issueTypeId: issueType.uuid }),
		})
	}, [issueType.uuid, navigate])

	const handleEdit = useCallback(() => {
		void navigate({
			from: "/$organizationId/$projectId",
			to: "/$organizationId/settings/issue-types/$issueTypeId",
			params: (params) => ({ ...params, issueTypeId: issueType.uuid }),
		})
	}, [issueType.uuid, navigate])

	return (
		<RadixCollapsible.Root open={!isHidden} onOpenChange={handleCollapsibleOpenChange} asChild>
			<Card role="listitem" className={cx("flex h-max w-full flex-col gap-4", interactiveCardCva())} size="sm">
				<RadixCollapsible.Trigger asChild>
					<div className="group flex w-full cursor-pointer items-center gap-2" tabIndex={0}>
						<div className="flex min-w-0 grow items-center gap-2">
							<IssueTypeIcon issueTypeId={issueType.uuid} />
							<Text className="truncate">{issueType.name}</Text>

							<Text size="sm" accentColor="base" weight="regular" className="whitespace-nowrap">
								{issues.length}
							</Text>
						</div>
						<ButtonGroup className="flex items-center gap-1" size="sm" variant="ghost" accentColor="base">
							<IconButton onClick={handleVisibilityButtonClicked}>
								<LuIcon icon={!isHidden ? modelIconMapping.visible : modelIconMapping.hidden} />
							</IconButton>

							<Menu.Root>
								<Menu.ClickTrigger>
									<IconButton aria-label="more" onClick={stopPropagation}>
										<LuIcon icon={modelIconMapping.more} />
									</IconButton>
								</Menu.ClickTrigger>
								<Menu.Content size="sm">
									<Menu.Item onSelect={handleView}>
										<LuIcon icon={modelIconMapping.expand} />
										View
									</Menu.Item>
									<Menu.Item onSelect={handleEdit}>
										<LuIcon icon={modelIconMapping.edit} />
										Edit
									</Menu.Item>
								</Menu.Content>
							</Menu.Root>
						</ButtonGroup>
					</div>
				</RadixCollapsible.Trigger>
				<RadixCollapsible.Content className="flex flex-col gap-4">
					<Separator size="full" />
					<ButtonGroup className="flex items-center gap-2" size="sm" variant="soft" accentColor="base">
						<IssueDataFilterMenu
							filterManager={filterManager}
							assignees={users}
							issueTypeFieldSchema={fieldsSchema}
							issueTypePriorities={issueTypePriorities}
							issueTypeStatuses={issueTypeStatuses}
							issueTypeIdentifiers={issueTypeIdentifiers}
							users={users}
							size="sm"
							align="start"
						>
							<Button onClick={stopPropagation}>
								<LuIcon icon={modelIconMapping.filter} />
								Filter
							</Button>
						</IssueDataFilterMenu>

						{filterManager.getFilters().length > 0 && (
							<IconButton onClick={filterManager.removeAll}>
								<LuIcon icon={modelIconMapping.close} />
							</IconButton>
						)}
					</ButtonGroup>

					<FilterRenderer filterManager={filterManager} size="xs" />
				</RadixCollapsible.Content>
			</Card>
		</RadixCollapsible.Root>
	)
})
IssueTypeVisibilityItem.displayName = "IssueTypeVisibilityItem"
